// ─── El portal con el aula virtual apagada ──────────────────────────────────
//
// «Mis cursos», el inicio y el calendario son del NÚCLEO: se ven siempre. Pero
// tienen partes que viven del aula —tareas, entregas, notas de tareas, módulos
// de contenido— y con el módulo `classroom` apagado esas partes hablaban de
// algo que el seminario no usa: «Todo al día con tus tareas», «Sin entregas
// pendientes», una pestaña «Tareas» vacía. Apagar esconde, no borra: los datos
// del aula siguen ahí y vuelven a verse al encenderlo.
//
// Con el aula encendida, todo queda exactamente como antes.

import { scheduleOccurrences } from '@dosfilos/domain';

/** Las pestañas de un curso que viven del aula. «Clases» y «Syllabus» no. */
export const CLASSROOM_COURSE_TABS: ReadonlySet<string> = new Set([
  'modules',
  'assignments',
  'grades',
]);

export function visibleCourseTabs<T extends { value: string }>(
  tabs: readonly T[],
  classroomOn: boolean,
): T[] {
  return classroomOn ? [...tabs] : tabs.filter((t) => !CLASSROOM_COURSE_TABS.has(t.value));
}

/** La pestaña con que abre un curso: «Módulos» con aula, «Clases» sin ella. */
export function defaultCourseTab(classroomOn: boolean): 'modules' | 'clases' {
  return classroomOn ? 'modules' : 'clases';
}

/** Qué bloques del inicio se muestran. */
export interface HomeBlocks {
  /** «Tu semana»: el resumen de tareas y sus atajos. */
  week: boolean;
  /** En la tarjeta de cada curso: entregas pendientes, «para corregir», nota nueva. */
  courseCardWork: boolean;
  /** «Pendientes»: sólo tareas. */
  pending: boolean;
  /** «Próximos 7 días» queda siempre: muestra clases (núcleo); sin aula, sin tareas. */
  next7Days: true;
}

export function homeBlocks(classroomOn: boolean): HomeBlocks {
  return { week: classroomOn, courseCardWork: classroomOn, pending: classroomOn, next7Days: true };
}

/** Los eventos de calendario que se muestran: sin aula, sólo las clases. */
export function calendarEventsFor<T extends { type: string }>(
  events: readonly T[],
  classroomOn: boolean,
): T[] {
  return classroomOn ? [...events] : events.filter((e) => e.type !== 'assignment');
}

// ─── Lo que sabe el núcleo de un curso ──────────────────────────────────────
//
// SIN AULA, EL CURSO NO QUEDA MUDO: muestra lo que sabe el núcleo —la sección,
// el período, el horario, el profesor y, cuando el acta está cerrada, la nota
// final—. Con aula, esto no se muestra: el curso es el de siempre.

export interface CourseScheduleEntry {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  location?: string;
}

export interface CoreCourseFacts {
  sectionCode: string | null;
  termName: string | null;
  facultyName: string | null;
  schedule: CourseScheduleEntry[];
  /** La nota del acta CERRADA; `null` mientras no se cierre. */
  finalGrade: { label: string; status: 'approved' | 'failed' | null } | null;
}

export function coreCourseFacts(
  section: {
    sectionCode?: string | null;
    termName?: string | null;
    facultyName?: string | null;
    schedule?: CourseScheduleEntry[];
    status?: string;
    finalGrade?: number | null;
    finalGradeLabel?: string | null;
    finalStatus?: 'enrolled' | 'approved' | 'failed';
  },
  classroomOn: boolean,
): CoreCourseFacts | null {
  if (classroomOn) return null;
  const closed = section.status === 'grades_closed';
  const label =
    section.finalGradeLabel ??
    (typeof section.finalGrade === 'number' ? String(section.finalGrade) : null);
  return {
    sectionCode: section.sectionCode ?? null,
    termName: section.termName ?? null,
    facultyName: section.facultyName || null,
    schedule: [...(section.schedule ?? [])].sort(
      (a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime),
    ),
    finalGrade:
      closed && label
        ? {
            label,
            status:
              section.finalStatus === 'approved' || section.finalStatus === 'failed'
                ? section.finalStatus
                : null,
          }
        : null,
  };
}

/** «19:00» en el formato de hora del seminario: «19:00» o «7:00 PM». */
export function wallTime(hhmm: string, fmt: '24h' | '12h'): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m || fmt === '24h') return hhmm;
  const h = Number(m[1]);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${suffix}`;
}

// ─── Sin aula, las clases salen del HORARIO de la sección (g4) ──────────────
//
// Con aula, el calendario son las sesiones del syllabus. Sin aula no hay
// syllabus que generar, pero cada sección sabe cuándo se dicta: su horario
// semanal entre sus fechas (o las del período). `scheduleOccurrences` (dominio)
// arma las ocurrencias; aquí se les da la forma de un evento del calendario.
// Un horario importado con término = inicio sale con `startOnly`; una sección
// sin horario no aparece. No se inventa nada.

export interface ScheduleSectionLike {
  id: string;
  courseName: string;
  courseCode: string;
  startDate?: string | null;
  endDate?: string | null;
  termId?: string | null;
  status?: string;
  finalStatus?: 'enrolled' | 'approved' | 'failed';
  schedule?: Array<{ dayOfWeek: number; startTime: string; endTime: string; location?: string }>;
}

export interface ScheduleCalendarEvent {
  type: 'session';
  id: string;
  sectionId: string;
  date: string;
  startTime: string;
  endTime?: string;
  title: string;
  courseName: string;
  courseCode: string;
  location?: string;
  /** Término = inicio (o sin término): se muestra solo la hora de inicio. */
  startOnly: boolean;
  source: 'schedule';
}

export function scheduleCalendarEvents(
  data:
    | {
        enrolledSections: readonly ScheduleSectionLike[];
        activeTerm?: { id: string; startDate: string; endDate: string } | null;
      }
    | undefined,
  from: string,
  to: string,
): ScheduleCalendarEvent[] {
  if (!data) return [];
  const out: ScheduleCalendarEvent[] = [];
  for (const s of data.enrolledSections) {
    // Solo lo que el alumno está cursando: ni lo ya cerrado por el acta ni una
    // sección cancelada.
    if (s.finalStatus && s.finalStatus !== 'enrolled') continue;
    if (s.status === 'cancelled') continue;
    const term = data.activeTerm && s.termId === data.activeTerm.id ? data.activeTerm : null;
    const occ = scheduleOccurrences({
      schedule: s.schedule,
      spanStart: s.startDate || term?.startDate,
      spanEnd: s.endDate || term?.endDate,
      from,
      to,
    });
    for (const o of occ) {
      out.push({
        type: 'session',
        id: `schedule-${s.id}-${o.date}-${o.entryIndex}`,
        sectionId: s.id,
        date: o.date,
        startTime: o.startTime,
        ...(o.endTime !== null ? { endTime: o.endTime } : {}),
        title: s.courseName,
        courseName: s.courseName,
        courseCode: s.courseCode,
        ...(o.location !== undefined ? { location: o.location } : {}),
        startOnly: o.endTime === null,
        source: 'schedule',
      });
    }
  }
  return out.sort((a, b) =>
    a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date),
  );
}

/** «YYYY-MM-DD» + n días, en calendario (sin zona horaria de por medio). */
export function addDaysYmd(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n, 12)).toISOString().slice(0, 10);
}
